import { useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function FaqPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: 'Quels sont les délais de livraison ?',
      answer: 'Toutes nos créations numériques sont livrées en moins de 24 heures directement dans votre boîte email. Pour les commandes passées avant 14h, la livraison est souvent effectuée le jour même. Vous recevrez une notification dès que votre création sera prête.',
    },
    {
      question: 'Quels formats de fichiers vais-je recevoir ?',
      answer: 'Selon le type de création choisi, vous recevrez vos fichiers en haute qualité : PDF pour les livres et invitations imprimables, JPG/PNG pour les portraits et personnages, et MP4 pour les vidéos animées. Tous les formats sont optimisés pour l\'impression et le partage numérique.',
    },
    {
      question: 'Puis-je demander des modifications après réception ?',
      answer: 'Absolument ! Nous offrons des modifications gratuites jusqu\'à votre entière satisfaction. Si un élément ne correspond pas à vos attentes, contactez-nous dans les 7 jours suivant la réception et nous apporterons les ajustements nécessaires sans frais supplémentaires.',
    },
    {
      question: 'Comment personnaliser ma création ?',
      answer: 'Le processus est très simple ! Après avoir choisi votre création, vous répondrez à un questionnaire guidé où vous pourrez indiquer le nom, l\'âge, l\'apparence physique, les préférences et tous les détails personnels. Notre mascotte Pixel vous accompagne à chaque étape pour vous aider.',
    },
    {
      question: 'Puis-je offrir une création en cadeau ?',
      answer: 'Oui, c\'est une excellente idée ! Lors de la commande, vous pouvez indiquer qu\'il s\'agit d\'un cadeau et nous enverrons la création à l\'adresse email de votre choix. Vous pouvez également ajouter un message personnalisé qui sera inclus dans l\'email de livraison.',
    },
    {
      question: 'Les créations sont-elles imprimables ?',
      answer: 'Oui, toutes nos créations sont fournies en haute résolution et sont parfaitement adaptées à l\'impression. Nous recommandons d\'utiliser du papier de qualité photo pour les meilleurs résultats. Les dimensions sont optimisées pour les formats standards d\'impression.',
    },
    {
      question: 'Quel est le mode de paiement accepté ?',
      answer: 'Nous acceptons tous les modes de paiement sécurisés via Stripe : cartes bancaires (Visa, Mastercard, American Express), Apple Pay, Google Pay. Toutes les transactions sont cryptées et 100% sécurisées. Aucun abonnement requis.',
    },
    {
      question: 'Puis-je commander plusieurs créations en même temps ?',
      answer: 'Bien sûr ! Vous pouvez ajouter autant de créations que vous le souhaitez à votre panier. Pour les commandes de plus de 3 créations, contactez-nous pour bénéficier d\'une remise spéciale sur votre commande groupée.',
    },
    {
      question: 'Les données personnelles sont-elles protégées ?',
      answer: 'La protection de vos données est notre priorité absolue. Toutes les informations personnelles que vous partagez sont cryptées et stockées de manière sécurisée. Nous ne partageons jamais vos données avec des tiers et vous pouvez demander leur suppression à tout moment.',
    },
    {
      question: 'Proposez-vous des créations pour les entreprises ?',
      answer: 'Oui ! Nous proposons des solutions personnalisées pour les entreprises : cartes de vœux animées, invitations d\'événements corporate, portraits d\'équipe illustrés, etc. Contactez-nous pour discuter de vos besoins spécifiques et obtenir un devis personnalisé.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-yellow-50 via-orange-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-6xl font-fredoka font-bold text-gray-900 mb-6">
              Questions{' '}
              <span className="bg-gradient-to-r from-primary-yellow via-orange-500 to-primary-coral bg-clip-text text-transparent">
                Fréquentes
              </span>
            </h1>
            <p className="text-xl font-quicksand text-gray-600 max-w-3xl mx-auto">
              Trouvez rapidement les réponses à vos questions sur nos créations et services
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Left - Pixel Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-1"
            >
              <div className="sticky top-32">
                <div className="w-full h-96 flex items-center justify-center mb-6">
                  <img
                    src="https://public.readdy.ai/ai/img_res/181ff5f4bf398109d53320e1eeace6e9.jpg"
                    alt="Pixel aide"
                    className="w-full h-full object-contain drop-shadow-2xl"
                  />
                </div>
                <div className="bg-gradient-to-br from-primary-blue to-purple-600 rounded-3xl p-6 text-white">
                  <h3 className="text-xl font-fredoka font-bold mb-3">
                    Besoin d'aide ?
                  </h3>
                  <p className="font-quicksand mb-4 text-white/90">
                    Notre équipe est là pour vous aider
                  </p>
                  <a
                    href="/contact"
                    className="block w-full px-6 py-3 bg-white text-gray-900 font-quicksand font-bold rounded-full text-center hover:shadow-soft-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    Nous contacter
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Right - FAQ Accordion */}
            <div className="lg:col-span-2">
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                  >
                    <div className="bg-white rounded-2xl shadow-soft border border-gray-100 overflow-hidden">
                      <button
                        onClick={() => setOpenIndex(openIndex === index ? null : index)}
                        className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors duration-300 cursor-pointer"
                      >
                        <span className="font-fredoka font-bold text-gray-900 text-lg pr-4">
                          {faq.question}
                        </span>
                        <div className={`flex-shrink-0 w-8 h-8 flex items-center justify-center bg-primary-blue/10 rounded-full transition-transform duration-300 ${
                          openIndex === index ? 'rotate-180' : ''
                        }`}>
                          <i className="ri-arrow-down-s-line text-primary-blue text-xl"></i>
                        </div>
                      </button>
                      {openIndex === index && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="px-6 pb-5"
                        >
                          <p className="font-quicksand text-gray-600 leading-relaxed">
                            {faq.answer}
                          </p>
                        </motion.div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-24 bg-neutral-lighter">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center bg-white rounded-4xl p-12 shadow-soft"
          >
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-gradient-to-br from-primary-mint to-green-600 rounded-3xl">
              <i className="ri-customer-service-line text-4xl text-white"></i>
            </div>
            <h2 className="text-3xl font-fredoka font-bold text-gray-900 mb-4">
              Vous ne trouvez pas votre réponse ?
            </h2>
            <p className="text-lg font-quicksand text-gray-600 mb-8">
              Notre équipe est disponible pour répondre à toutes vos questions
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gray-900 text-white font-quicksand font-bold rounded-full hover:shadow-soft-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line"></i>
              <span>Contactez-nous</span>
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}